/* Common Imports */

import { styled } from "@mui/system";
import React from "react";

/* Redux Imports */

import { useRouter } from "next/router";
import { CreateWebsiteReportThunk } from "../../redux-magic/thunks";

/* Component Imports */

import {
	Alert,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
	Typography,
} from "@mui/material";
import Snackbar, { SnackbarOrigin } from "@mui/material/Snackbar";
import BeegruButton from "../common-components/buttons/BeegruButton";

/* Icon Imports */

import { Close } from "@mui/icons-material";

interface State extends SnackbarOrigin {
	openSnack: boolean;
}

/* Styled Components */

const CustomDialog = styled(Dialog)(({ theme }) => ({
	"& .MuiDialog-paper": {
		width: "calc(100% - 1rem)",
		margin: "0rem",
	},
}));

const CustomDialogTitle = styled(DialogTitle)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	padding: "0.5rem 1rem 0.5rem 1rem",
	background: theme.palette.background.paper,
}));

const CustomDialogContent = styled(DialogContent)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.75rem",
	padding: "2rem 1rem 1rem 1rem",
	background: theme.palette.background.paper,
	[theme.breakpoints.down("sm")]: {
		flexDirection: "column",
	},
}));

const CustomDialogActions = styled(DialogActions)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	width: "100%",
	gap: "1rem",
	padding: "0rem 1rem 1rem 0rem",
	background: theme.palette.background.paper,
}));

const Snack = styled(Snackbar)(({ theme }) => ({
	[theme.breakpoints.down("md")]: {
		bottom: "5rem",
		borderRadius: "0.5rem",
	},
}));

const ReportModal = ({
	open,
	dispatch,
	handleClose,
	session,
	title,
}: {
	open: boolean;
	handleClose: any;
	dispatch: Function;
	session: any;
	title: string;
}) => {
	const router = React.useRef(useRouter()).current;

	const reportTitleKannada: string =
		title === "property"
			? "ಪ್ರಾಪರ್ಟಿಯನ್ನು"
			: title === "post"
				? "ಪೋಸ್ಟ್"
				: title === "Agent"
					? "ಏಜೆಂಟನ್ನು"
					: title === "Developer"
						? "ಡೆವಲಪರ್ ಅನ್ನು"
						: title === "Landowner"
							? "ಜಮೀನುದಾರನನ್ನು"
							: title === "Professional"
								? "ಪ್ರೊಫೆಶನಲ್ ಪ್ರೊಫೈಲ್ ಅನ್ನು "
								: title === "service"
									? "ಸೇವೆಯನ್ನು"
									: title === "User"
										? "ಯೂಸರ್ ಅನ್ನು"
										: "";

	const reportTitleArabic: string =
		title === "property"
			? "الممتلكات"
			: title === "post"
				? "المشاركة"
				: title === "Agent"
					? "الوكيل"
					: title === "Developer"
						? "المطور"
						: title === "Landowner"
							? "مالك الأرض"
							: title === "Professional"
								? "المحترف"
								: title === "service"
									? "الخدمة"
									: title === "User"
										? "المستخدم"
										: "";

	const localLang = {
		title:
			router.locale === "kn-IN"
				? reportTitleKannada + " ರಿಪೋಟ್ ಮಾಡಿ"
				: router.locale === "ar-AE"
					? " "
					: "Report " + title.toLowerCase(),
		alertMessage:
			router.locale === "kn-IN"
				? "ನಿಮ್ಮ ವಿಚಾರವನ್ನು ಸಮರ್ಥವಾಗಿ ಸಲ್ಲಿಸಲಾಗಿದೆ."
				: router.locale === "ar-AE"
					? ""
					: "Your report has been successfully submitted.",
		//goto line 198
		placeHolderText:
			router.locale === "kn-IN"
				? " ನಿಮ್ಮ ಸಮಸ್ಯೆಯನ್ನು ಇಲ್ಲಿ ವಿವರಿಸಿ..."
				: router.locale === "ar-AE"
					? ""
					: "Describe your issue here...",
		submitText: router.locale === "kn-IN" ? "ಸಲ್ಲಿಸಿ" : router.locale === "ar-AE" ? "" : "Submit",
		submittingText:
			router.locale === "kn-IN" ? "ಸಲ್ಲಿಸುತ್ತಿದ್ದೇನೆ..." : router.locale === "ar-AE" ? "" : "Submitting...",
	};

	const [description, setDescription] = React.useState("");
	const [submitting, setSubmitting] = React.useState<boolean>(false);

	const submit = async () => {
		setSubmitting(true);
		await dispatch(
			CreateWebsiteReportThunk({
				report_type: title,
				description: description,
				is_business_profile: session
					? session.user
						? session.user.dbUser
							? session.user.dbUser.profile_context
								? session.user.dbUser.profile_context.is_business_profile
								: false
							: false
						: false
					: false,
				user_id: session ? (session.user ? (session.user.dbUser ? session.user.dbUser._id : "") : "") : "",
				business_profile_id: session
					? session.user
						? session.user.dbUser
							? session.user.dbUser.profile_context
								? session.user.dbUser.profile_context.business_profile_id
								: ""
							: ""
						: ""
					: "",
				url: router.asPath,
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
			}),
		);
		setSubmitting(false);
		handleClickSnack({ vertical: "bottom", horizontal: "left" });
		handleClose();
		setDescription("");
	};

	const [state, setState] = React.useState<State>({
		openSnack: false,
		vertical: "top",
		horizontal: "center",
	});

	const { vertical, horizontal, openSnack } = state;

	const handleClickSnack = (newState: SnackbarOrigin) => {
		setState({ ...newState, openSnack: true });
	};

	const handleCloseSnack = () => {
		setState({ ...state, openSnack: false });
	};

	return (
		<React.Fragment>
			<Snack
				// anchorOrigin={{ vertical, horizontal }}
				open={openSnack}
				autoHideDuration={3000}
				onClose={handleCloseSnack}
			>
				<Alert
					onClose={handleCloseSnack}
					severity="success"
					sx={{ width: "100%" }}
				>
					{localLang.alertMessage}
				</Alert>
			</Snack>

			<CustomDialog
				open={open}
				onClose={handleClose}
				maxWidth="sm"
				fullWidth={true}
				disableScrollLock={true}
			>
				<CustomDialogTitle>
					{localLang.title}
					<IconButton
						aria-label="close"
						onClick={handleClose}
					>
						<Close />
					</IconButton>
				</CustomDialogTitle>

				<CustomDialogContent>
					<Typography variant="body2">
						{router.locale === "kn-IN"
							? " ಈ ಜಾಲತಾಣದಲ್ಲಿ ನಿಮಗೆ ಏನು ಸಮಸ್ಯೆಗಳಿವೆ ಎಂದು ಕೇಳಿದ್ದೇವೆ. ದಯವಿಟ್ಟು ನಿಮ್ಮ ಸಮಸ್ಯೆಯನ್ನು ವಿವರಿಸಲು ನಮಗೆ ಸಹಾಯ ಮಾಡಿ."
							: router.locale === "ar-AE"
								? ""
								: "We're sorry to hear that you're facing issues with this " +
									title.toLowerCase() +
									". Please help us understand the problem."}
					</Typography>

					<TextField
						id="outlined-multiline-static"
						placeholder={localLang.placeHolderText}
						multiline
						rows={4}
						value={description}
						disabled={submitting}
						onChange={(e) => {
							setDescription(e.target.value);
						}}
						sx={{ width: "100%" }}
					/>
				</CustomDialogContent>

				<CustomDialogActions>
					<BeegruButton
						flavor="primary"
						variant="contained"
						onClick={() => {
							submit();
						}}
						disabled={submitting}
						name="submit"
						type="button"
					>
						{submitting ? localLang.submittingText : localLang.submitText}
					</BeegruButton>
				</CustomDialogActions>
			</CustomDialog>
		</React.Fragment>
	);
};

export default ReportModal;
